import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INDIAN_STATES = [
  { name: 'Andhra Pradesh', code: 'AP' },
  { name: 'Arunachal Pradesh', code: 'AR' },
  { name: 'Assam', code: 'AS' },
  { name: 'Bihar', code: 'BR' },
  { name: 'Chhattisgarh', code: 'CG' },
  { name: 'Goa', code: 'GA' },
  { name: 'Gujarat', code: 'GJ' },
  { name: 'Haryana', code: 'HR' },
  { name: 'Himachal Pradesh', code: 'HP' },
  { name: 'Jharkhand', code: 'JH' },
  { name: 'Karnataka', code: 'KA' },
  { name: 'Kerala', code: 'KL' },
  { name: 'Madhya Pradesh', code: 'MP' },
  { name: 'Maharashtra', code: 'MH' },
  { name: 'Manipur', code: 'MN' },
  { name: 'Meghalaya', code: 'ML' },
  { name: 'Mizoram', code: 'MZ' },
  { name: 'Nagaland', code: 'NL' },
  { name: 'Odisha', code: 'OR' },
  { name: 'Punjab', code: 'PB' },
  { name: 'Rajasthan', code: 'RJ' },
  { name: 'Sikkim', code: 'SK' },
  { name: 'Tamil Nadu', code: 'TN' },
  { name: 'Telangana', code: 'TS' },
  { name: 'Tripura', code: 'TR' },
  { name: 'Uttar Pradesh', code: 'UP' },
  { name: 'Uttarakhand', code: 'UK' },
  { name: 'West Bengal', code: 'WB' },
  { name: 'Andaman and Nicobar Islands', code: 'AN' },
  { name: 'Chandigarh', code: 'CH' },
  { name: 'Dadra and Nagar Haveli and Daman and Diu', code: 'DH' },
  { name: 'Delhi', code: 'DL' },
  { name: 'Jammu and Kashmir', code: 'JK' },
  { name: 'Ladakh', code: 'LA' },
  { name: 'Lakshadweep', code: 'LD' },
  { name: 'Puducherry', code: 'PY' }
];

const AddressForm = ({ address, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    label: '',
    recipient_name: '',
    phone: '',
    email: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    state_code: '',
    postal_code: '',
    country: 'IN',
    address_type: 'shipping',
    is_default_shipping: false,
    is_residential: true,
    delivery_instructions: ''
  });

  const [validatingPincode, setValidatingPincode] = useState(false);

  useEffect(() => {
    if (address) {
      setFormData({
        label: address.label || '',
        recipient_name: address.recipient_name || '',
        phone: address.phone || '',
        email: address.email || '',
        address_line1: address.address_line1 || '',
        address_line2: address.address_line2 || '',
        city: address.city || '',
        state: address.state || '',
        state_code: address.state_code || '',
        postal_code: address.postal_code || '',
        country: address.country || 'IN',
        address_type: address.address_type || 'shipping',
        is_default_shipping: address.is_default_shipping || false,
        is_residential: address.is_residential ?? true,
        delivery_instructions: address.delivery_instructions || ''
      });
    }
  }, [address]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleStateChange = (e) => {
    const selectedState = INDIAN_STATES.find(s => s.name === e.target.value);
    setFormData(prev => ({
      ...prev,
      state: e.target.value,
      state_code: selectedState?.code || ''
    }));
  };

  const handlePincodeBlur = async () => {
    const pincode = formData.postal_code;
    if (!pincode || pincode.length !== 6) return;

    setValidatingPincode(true);
    try {
      const response = await axios.get(`${API}/pincode/validate/${pincode}`);
      
      if (response.data.success) {
        const selectedState = INDIAN_STATES.find(s => s.name === response.data.state);
        
        setFormData(prev => ({
          ...prev,
          city: response.data.city || prev.city,
          state: response.data.state || prev.state,
          state_code: selectedState?.code || prev.state_code
        }));
        
        toast.success('PIN code validated');
      } else {
        toast.error('Invalid PIN code');
      }
    } catch (error) {
      console.error('PIN code validation error:', error);
    } finally {
      setValidatingPincode(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.recipient_name || !formData.phone || !formData.address_line1 || 
        !formData.city || !formData.state || !formData.postal_code) {
      toast.error('Please fill all required fields');
      return;
    }

    // Phone validation (Indian format)
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(formData.phone)) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    // PIN code validation
    if (formData.postal_code.length !== 6 || !formData.postal_code.match(/^\d{6}$/)) {
      toast.error('Please enter a valid 6-digit PIN code');
      return;
    }

    onSubmit(formData);
  };

  return (
    <div className="glass-effect p-8 rounded-xl max-w-3xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">
          {address ? 'Edit Address' : 'Add New Address'}
        </h2>
        <button onClick={onCancel} className="text-gray-400 hover:text-white">
          <X size={24} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Label (Optional) */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Label (Optional)
          </label>
          <input
            type="text"
            name="label"
            value={formData.label}
            onChange={handleChange}
            placeholder="e.g., Home, Office, Mom's Place"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-red-500"
          />
        </div>

        {/* Recipient Name */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="recipient_name"
            value={formData.recipient_name}
            onChange={handleChange}
            required
            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-red-500"
          />
        </div>

        {/* Phone & Email */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="10-digit mobile number"
              required
              maxLength="10"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-red-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">
              Email (Optional)
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-red-500"
            />
          </div>
        </div>

        {/* Address Lines */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Address Line 1 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="address_line1"
            value={formData.address_line1}
            onChange={handleChange}
            placeholder="House/Flat No., Building Name, Street"
            required
            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-red-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Address Line 2 (Optional)
          </label>
          <input
            type="text"
            name="address_line2"
            value={formData.address_line2}
            onChange={handleChange}
            placeholder="Locality, Area, Landmark"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-red-500"
          />
        </div>

        {/* PIN Code */}
        <div>
          <label className="block text-sm font-medium mb-2">
            PIN Code <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="postal_code"
            value={formData.postal_code}
            onChange={handleChange}
            // onBlur={handlePincodeBlur}
            placeholder="6-digit PIN code"
            required
            maxLength="6"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-red-500"
          />
          {validatingPincode && (
            <p className="text-sm text-gray-400 mt-1">Validating PIN code...</p>
          )}
        </div>

        {/* City & State */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              City <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              required
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-red-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">
              State <span className="text-red-500">*</span>
            </label>
            <select
              name="state"
              value={formData.state}
              onChange={handleStateChange}
              required
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-red-500"
            >
              <option value="">Select State</option>
              {INDIAN_STATES.map(state => (
                <option key={state.code} value={state.name}>
                  {state.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Delivery Instructions */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Delivery Instructions (Optional)
          </label>
          <textarea
            name="delivery_instructions"
            value={formData.delivery_instructions}
            onChange={handleChange}
            placeholder="Any special instructions for delivery"
            rows="3"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-red-500 resize-none"
          />
        </div>

        {/* Checkboxes */}
        <div className="space-y-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              name="is_default_shipping"
              checked={formData.is_default_shipping}
              onChange={handleChange}
              className="mr-3 w-5 h-5 accent-red-500"
            />
            <span className="text-sm">Set as default shipping address</span>
          </label>

          <label className="flex items-center">
            <input
              type="checkbox"
              name="is_residential"
              checked={formData.is_residential}
              onChange={handleChange}
              className="mr-3 w-5 h-5 accent-red-500"
            />
            <span className="text-sm">This is a residential address</span>
          </label>
        </div>

        {/* Buttons */}
        <div className="flex space-x-4 pt-4">
          <button
            type="submit"
            className="flex-1 btn-primary py-3"
          >
            {address ? 'Update Address' : 'Save Address'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 border border-white/20 rounded-lg hover:bg-white/5 transition py-3"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddressForm;